import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { LogOut, Check, ExternalLink } from 'lucide-react';
import ThemeToggle from '@/components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared sticky header used by every judge screen so navigation actions
// (Kedudukan Saya, Tukar Pasukan, Tukar Jantina) are always reachable.
function JudgeNav({
  user,
  subtitle,
  onLogout,
  onChangeTeam,
  onChangeGender,
  onBack,
  backLabel,
  sticky = false,
}) {
  const openMyRankings = () => {
    const url = `${window.location.origin}/judge?view=rankings`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <header
      className={`border-b border-zinc-800 bg-[#18181b] ${sticky ? 'sticky top-0 z-40' : ''}`}
    >
      <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-white">Piala Suara Serumpun 2026</h1>
          <p className="text-xs text-zinc-500">{subtitle || user.name}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <ThemeToggle />
          <Button
            onClick={openMyRankings}
            data-testid="view-my-rankings-button"
            variant="ghost"
            className="text-zinc-400 hover:text-white text-xs gap-1"
            title="Buka Kedudukan Saya dalam tab baharu"
          >
            Kedudukan Saya
            <ExternalLink className="h-3 w-3" />
          </Button>
          <Button
            onClick={onChangeTeam}
            data-testid="back-to-teams-button"
            variant="ghost"
            className="text-zinc-400 hover:text-white text-xs"
          >
            Tukar Pasukan
          </Button>
          <Button
            onClick={onChangeGender}
            data-testid="change-gender-button"
            variant="ghost"
            className="text-zinc-400 hover:text-white text-xs"
          >
            Tukar Jantina
          </Button>
          {onBack && (
            <Button
              onClick={onBack}
              data-testid="back-button"
              variant="ghost"
              className="text-zinc-400 hover:text-white text-xs"
            >
              {backLabel || 'Kembali'}
            </Button>
          )}
          <Button
            onClick={onLogout}
            data-testid="logout-button"
            variant="ghost"
            className="text-zinc-400 hover:text-white"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}

// Input that accepts digits and a single decimal point, strips leading zeros,
// and preserves partial entries like "7." while the user is typing. A `null`
// value renders as an empty string so the field starts blank.
function NumericInput({ value, onChange, max, className, ariaLabel, dataTestid }) {
  const [text, setText] = useState(value === null || value === undefined ? '' : String(value));
  const [focused, setFocused] = useState(false);

  // Sync external value -> visible text when the user is not typing in this field.
  useEffect(() => {
    if (!focused) {
      setText(value === null || value === undefined ? '' : String(value));
    }
  }, [value, focused]);

  const handleChange = (e) => {
    const raw = e.target.value;
    if (!/^\d*\.?\d*$/.test(raw)) return;
    let cleaned = raw;
    if (/^0\d/.test(cleaned)) {
      cleaned = cleaned.replace(/^0+/, '');
    }
    setText(cleaned);
    if (cleaned === '' || cleaned === '.') {
      onChange(null);
      return;
    }
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed)) {
      onChange(null);
      return;
    }
    // No clamping: we let the value go above max so the submit-time validator
    // can flag it. Negatives are clipped at 0.
    onChange(Math.max(0, parsed));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      pattern="[0-9]*\.?[0-9]*"
      aria-label={ariaLabel}
      data-testid={dataTestid}
      value={text}
      placeholder=""
      onFocus={(e) => {
        setFocused(true);
        e.target.select();
      }}
      onBlur={() => {
        setFocused(false);
        setText(value === null || value === undefined ? '' : String(value));
      }}
      onChange={handleChange}
      className={className}
    />
  );
}

function SubcategoryInput({ subcategory, value, onChange }) {
  const numericValue = typeof value === 'number' ? value : 0;
  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-800">
      <div className="flex-1">
        <label className="text-sm text-zinc-300">{subcategory.name}</label>
        <div className="text-xs text-zinc-500">Maksimum: {subcategory.max_score}</div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={() => onChange(Math.max(0, numericValue - 0.5))}
          className="h-8 w-8 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm p-0"
        >
          -
        </Button>
        <NumericInput
          value={value}
          onChange={onChange}
          max={subcategory.max_score}
          ariaLabel={subcategory.name}
          className="w-16 bg-zinc-900 border border-zinc-700 focus:border-[#DFFF00] focus:outline-none text-center text-white font-mono rounded-sm py-1"
        />
        <Button
          type="button"
          onClick={() => onChange(Math.min(subcategory.max_score, numericValue + 0.5))}
          className="h-8 w-8 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm p-0"
        >
          +
        </Button>
      </div>
    </div>
  );
}

// Self-contained scoring form for a single category. Renders subcategories,
// deduction, notes, and its own Submit/Update button so the parent can stack
// multiple of these in one page for judges with multiple assigned categories.
function CategoryScoringForm({ category, subcategories, existingScore, userName, teamId, onSaved, index, total, locked, bengkelMarks }) {
  // localStorage key for unsaved (draft) inputs — scoped to judge + team + category
  // so a judge can flip between teams and keep typing without losing progress.
  const draftKey = `draft:${userName}:${teamId}:${category.id}`;

  const readDraft = () => {
    try {
      const raw = localStorage.getItem(draftKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const buildInitialScores = (savedSource, draftSource) => {
    const initial = {};
    subcategories.forEach((sub) => {
      const draftVal = draftSource && draftSource.scores ? draftSource.scores[sub.id] : undefined;
      const savedVal = savedSource ? savedSource[sub.id] : undefined;
      // Draft (unsaved edits) wins; otherwise saved DB value; otherwise blank.
      const v = draftVal !== undefined ? draftVal : savedVal;
      initial[sub.id] = (v === undefined || v === null) ? null : v;
    });
    return initial;
  };

  const initialDraft = (typeof window !== 'undefined') ? readDraft() : null;
  const [scores, setScores] = useState(() =>
    buildInitialScores(existingScore ? existingScore.subcategory_scores : null, initialDraft)
  );
  const [deduction, setDeduction] = useState(() =>
    initialDraft && initialDraft.deduction !== undefined
      ? initialDraft.deduction
      : (existingScore ? existingScore.deduction || 0 : 0)
  );
  const [notes, setNotes] = useState(() =>
    initialDraft && initialDraft.notes !== undefined
      ? initialDraft.notes
      : (existingScore ? existingScore.notes || '' : '')
  );
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Reset local state when we switch to a different team or the saved score changes externally.
  // Drafts in localStorage take precedence so unsaved edits aren't lost.
  useEffect(() => {
    const draft = readDraft();
    setScores(buildInitialScores(existingScore ? existingScore.subcategory_scores : null, draft));
    setDeduction(
      draft && draft.deduction !== undefined
        ? draft.deduction
        : (existingScore ? existingScore.deduction || 0 : 0)
    );
    setNotes(
      draft && draft.notes !== undefined
        ? draft.notes
        : (existingScore ? existingScore.notes || '' : '')
    );
    setValidationError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, existingScore?.id]);

  // Persist any pending edits to localStorage so navigating between teams
  // (or refreshing) doesn't lose work. The draft is cleared on successful submit.
  useEffect(() => {
    const anyScoreEntered = Object.values(scores).some((v) => v !== null && v !== undefined);
    const hasDeduction = deduction !== 0;
    const hasNotes = notes && notes.length > 0;
    if (!anyScoreEntered && !hasDeduction && !hasNotes) {
      // Nothing to keep — make sure no stale draft lingers.
      try { localStorage.removeItem(draftKey); } catch {}
      return;
    }
    try {
      localStorage.setItem(draftKey, JSON.stringify({ scores, deduction, notes }));
    } catch {}
  }, [scores, deduction, notes, draftKey]);

  const hasScored = Boolean(existingScore);

  // ----- Per-judge customisation for the two "Pilihan Juri" categories -----
  // a) The first subcategory ("Kesesuaian dan keberkesanan irama dengan ...")
  //    has a different label tail per judge AND per category. Munir & Isa see
  //    "Tok Juara" in Lagu Juara but "Tukang Karut" in Lagu Karut.
  // b) The second subcategory ("Keaslian dan kreativiti") is only visible to
  //    Munir & Isa — for everyone else it's hidden AND not submitted, so the
  //    category max effectively drops from 20 → 10.
  const SIMPLE_SUM_CATS = new Set([
    'Lagu Juara Terbaik Pilihan Juri',
    'Lagu Karut Pilihan Juri',
  ]);
  const IRAMA_SUFFIX_BY_CAT_AND_JUDGE = {
    'Lagu Juara Terbaik Pilihan Juri': {
      Djohan: 'seni kata',
      Yahya: 'muzik',
      Elfee: 'Tok Juara',
      Isa: 'Tukang Karut',
      // Soffian → default ("awak-awak")
    },
    'Lagu Karut Pilihan Juri': {
      Djohan: 'seni kata',
      Yahya: 'muzik',
      Elfee: 'Tok Juara',
      Isa: 'Tukang Karut',
      Soffian: 'awak-awak',
    },
  };
  const KEASLIAN_VISIBLE_TO = new Set(['Elfee', 'Isa']);
  // Per-category override for who may see the "Keaslian dan kreativiti" row(s).
  const KEASLIAN_VISIBLE_BY_CAT = {
    'Lagu Juara Terbaik Pilihan Juri': new Set(['Elfee', 'Isa']),
    'Lagu Karut Pilihan Juri': new Set(['Isa', 'Elfee']),
  };

  // Judges whose "Kesesuaian dan keberkesanan irama..." row is HIDDEN in a
  // Pilihan Juri category (because they're judging the opposite specialty).
  // Elfee (Juara/Tok Juara specialist) → hide the Kesesuaian row on Lagu Karut.
  // Isa   (Karut specialist)           → hide the Kesesuaian row on Lagu Juara.
  const KESESUAIAN_HIDDEN_BY_CAT = {
    'Lagu Juara Terbaik Pilihan Juri': new Set(['Isa']),
    'Lagu Karut Pilihan Juri': new Set(['Elfee']),
  };

  const isVisibleSubcat = (subcat) => {
    if (SIMPLE_SUM_CATS.has(category.name)) {
      if (
        subcat.name.includes('Keaslian dan kreativiti') &&
        !(KEASLIAN_VISIBLE_BY_CAT[category.name] || KEASLIAN_VISIBLE_TO).has(userName)
      ) {
        return false;
      }
      if (
        subcat.name.includes('Kesesuaian dan keberkesanan irama') &&
        (KESESUAIAN_HIDDEN_BY_CAT[category.name] || new Set()).has(userName)
      ) {
        return false;
      }
    }
    return true;
  };
  const visibleSubcats = subcategories.filter(isVisibleSubcat);
  const visibleMaxScore = visibleSubcats.reduce((a, s) => a + s.max_score, 0);
  const subcategoryTotal = visibleSubcats.reduce(
    (a, s) => a + (typeof scores[s.id] === 'number' ? scores[s.id] : 0),
    0
  );
  const currentTotal = subcategoryTotal - deduction;

  const handleSubmit = async () => {
    if (locked) {
      toast.error('Pemarkahan sedang dikunci oleh admin');
      return;
    }
    const visibleSubcategories = subcategories.filter(isVisibleSubcat);
    const missing = visibleSubcategories.filter((subcat) => {
      const v = scores[subcat.id];
      return v === null || v === undefined;
    });
    if (missing.length > 0) {
      setValidationError(
        `Sila isi semua subkategori. Tertinggal: ${missing.map((f) => f.name).join(', ')}`
      );
      toast.error(`[${category.name}] Sila isi semua subkategori sebelum menghantar`);
      return;
    }
    const overMax = visibleSubcategories.filter((subcat) => {
      const v = scores[subcat.id];
      return typeof v === 'number' && v > subcat.max_score;
    });
    if (overMax.length > 0) {
      setValidationError(
        overMax
          .map(
            (s) =>
              `${s.name}: ${scores[s.id]} melebihi markah maksimum ${s.max_score}`
          )
          .join(' • ')
      );
      toast.error(`[${category.name}] Beberapa markah melebihi maksimum subkategori`);
      return;
    }
    setValidationError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Only persist scores for VISIBLE subcategories so hidden ones (e.g.
      // "Keaslian dan kreativiti" for non-Munir/Isa judges) don't end up in DB.
      const visibleScores = {};
      visibleSubcategories.forEach((sub) => {
        visibleScores[sub.id] = scores[sub.id];
      });
      await axios.post(
        `${API}/scores`,
        {
          team_id: teamId,
          category_id: category.id,
          subcategory_scores: visibleScores,
          deduction,
          notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Markah ${category.name} dihantar!`);
      // Saved successfully — wipe the local draft so the next mount uses the DB row.
      try { localStorage.removeItem(draftKey); } catch {}
      if (onSaved) await onSaved();
    } catch (error) {
      toast.error(`Gagal menghantar markah ${category.name}`);
    } finally {
      setLoading(false);
    }
  };

  const renderSubcategories = () => {
    // Helper: render a grouped section (yellow-bordered card with header + short-labelled rows)
    const renderGroup = (title, items, shortNameFn, extraClass = 'mb-4') => {
      if (!items || items.length === 0) return null;
      return (
        <div
          key={`group-${title}`}
          className={`bg-zinc-900/50 border-l-4 border-[#DFFF00] p-4 ${extraClass} rounded-sm`}
        >
          <div className="text-sm text-[#DFFF00] font-semibold mb-3 uppercase tracking-wide">
            {title}
          </div>
          <div className="space-y-2 pl-2">
            {items.map((subcat) => (
              <SubcategoryInput
                key={subcat.id}
                subcategory={{ ...subcat, name: shortNameFn(subcat.name) }}
                value={scores[subcat.id] ?? null}
                onChange={(val) => setScores({ ...scores, [subcat.id]: val })}
              />
            ))}
          </div>
        </div>
      );
    };

    const renderSingle = (subcat) => (
      <SubcategoryInput
        key={subcat.id}
        subcategory={subcat}
        value={scores[subcat.id] ?? null}
        onChange={(val) => setScores({ ...scores, [subcat.id]: val })}
      />
    );

    // Helper: strip a known prefix from subcategory name to produce a short label
    const stripPrefix = (prefix) => (name) =>
      name.startsWith(prefix) ? name.slice(prefix.length) : name;

    // Helper: turn "Chorus"/"Verse 1"/"Verse 2" substrings into short labels
    const chorusVerseShort = (n) =>
      n.includes('Chorus') ? 'Chorus' : n.includes('Verse 1') ? 'Verse 1' : 'Verse 2';

    // =====================================================================
    // PALUAN category — grouped "Jogho" / "Karut" sections + standalone rows
    // =====================================================================
    if (category.name === 'Paluan') {
      const pickPrefix = (prefix) => subcategories.filter((s) => s.name.startsWith(prefix));
      const joghoGroup = pickPrefix('Jogho - ');
      const karutGroup = pickPrefix('Karut - ');
      const groupedIds = new Set([...joghoGroup, ...karutGroup].map((s) => s.id));
      const standaloneRows = subcategories.filter((s) => !groupedIds.has(s.id));

      return (
        <>
          {renderGroup('Jogho', joghoGroup, stripPrefix('Jogho - '))}
          {renderGroup('Karut', karutGroup, stripPrefix('Karut - '))}
          <div className="space-y-1">{standaloneRows.map(renderSingle)}</div>
        </>
      );
    }

    // =====================================================================
    // JUARA category — grouped "Mutu Suara" / "Gaya Suara" sections
    // =====================================================================
    if (category.name === 'Juara') {
      const stripSection = (name) =>
        name.replace(/ - (Chorus|Verse 1|Verse 2)$/, '');
      const sectionOf = (name) =>
        name.includes('Chorus') ? 'Chorus' : name.includes('Verse 1') ? 'Verse 1' : name.includes('Verse 2') ? 'Verse 2' : null;
      const inSection = (sec) =>
        subcategories
          .filter((s) => sectionOf(s.name) === sec)
          .sort((a, b) =>
            (a.name.includes('Mutu Suara') ? 0 : 1) - (b.name.includes('Mutu Suara') ? 0 : 1),
          );
      const sectionedIds = new Set(
        subcategories.filter((s) => sectionOf(s.name)).map((s) => s.id),
      );
      const otherSubcats = subcategories.filter((s) => !sectionedIds.has(s.id));

      return (
        <>
          {renderGroup('Chorus', inSection('Chorus'), stripSection)}
          {renderGroup('Verse 1', inSection('Verse 1'), stripSection)}
          {renderGroup('Verse 2', inSection('Verse 2'), stripSection)}
          <div className="space-y-1">{otherSubcats.map(renderSingle)}</div>
        </>
      );
    }

    // =====================================================================
    // AWAK-AWAK category — grouped by section (Juara / Karut sub-sections /
    // Penghafalan Seni Kata) with a couple of standalone rows at the end.
    // =====================================================================
    if (category.name === 'Awak-Awak') {
      const pick = (prefix) => subcategories.filter((s) => s.name.startsWith(prefix));

      const juaraGroup = pick('Juara - ');
      const yengkiGroup = pick('Karut - Yengki - ');
      const kelantanGroup = pick('Karut - Kelantan - ');
      const temasekGroup = pick('Karut - Temasek - ');
      const matangGroup = pick('Karut - Matang - ');
      const penutupGroup = pick('Karut - Penutup - ');
      const penghafalanGroup = pick('Penghafalan Seni Kata - ');
      const groupedIds = new Set(
        [
          ...juaraGroup,
          ...yengkiGroup,
          ...kelantanGroup,
          ...temasekGroup,
          ...matangGroup,
          ...penutupGroup,
          ...penghafalanGroup,
        ].map((s) => s.id),
      );
      const standaloneRows = subcategories.filter((s) => !groupedIds.has(s.id));

      return (
        <>
          {userName === 'Soffian' && (
            <div className="mb-4 px-3 py-2 bg-zinc-900/60 border-l-2 border-[#DFFF00]/60 rounded-sm text-xs text-zinc-400 italic">
              <span className="text-[#DFFF00] not-italic font-semibold">Dokongan Suara:</span>{' '}
              (Projeksi, kesatuan, intonasi, pernafasan, kawalan, penghayatan dalam persembahan)
            </div>
          )}
          {renderGroup('Juara', juaraGroup, stripPrefix('Juara - '))}
          {renderGroup('Karut — Yengki', yengkiGroup, stripPrefix('Karut - Yengki - '))}
          {renderGroup('Karut — Kelantan', kelantanGroup, stripPrefix('Karut - Kelantan - '))}
          {renderGroup('Karut — Temasek', temasekGroup, stripPrefix('Karut - Temasek - '))}
          {renderGroup('Karut — Matang', matangGroup, stripPrefix('Karut - Matang - '))}
          {renderGroup('Karut — Penutup', penutupGroup, stripPrefix('Karut - Penutup - '))}
          {renderGroup(
            'Penghafalan Seni Kata',
            penghafalanGroup,
            stripPrefix('Penghafalan Seni Kata - '),
          )}
          <div className="space-y-1">{standaloneRows.map(renderSingle)}</div>
        </>
      );
    }

    // =====================================================================
    // TUKANG KARUT category — group the five "Penghafalan <section>" rows
    // into one Penghafalan block; everything else stays flat, original order.
    // =====================================================================
    if (category.name === 'Tukang Karut') {
      const penghafalanGroup = subcategories.filter((s) => s.name.startsWith('Penghafalan '));
      const groupedIds = new Set(penghafalanGroup.map((s) => s.id));
      const before = [];
      let inserted = false;
      const ordered = [];
      subcategories.forEach((s) => {
        if (groupedIds.has(s.id)) {
          if (!inserted) {
            ordered.push({ __group: true });
            inserted = true;
          }
        } else {
          ordered.push(s);
        }
      });
      // Fallback: if the Penghafalan rows were first/never hit the else branch,
      // still append the group at the end.
      if (!inserted && penghafalanGroup.length > 0) {
        ordered.push({ __group: true });
      }

      return (
        <>
          {ordered.map((item, idx) => {
            if (item && item.__group) {
              return (
                <div key={`penghafalan-group-${idx}`}>
                  {renderGroup(
                    'Penghafalan',
                    penghafalanGroup,
                    stripPrefix('Penghafalan '),
                    'my-2',
                  )}
                </div>
              );
            }
            return renderSingle(item);
          })}
        </>
      );
      // (before intentionally unused; kept for readability)
      // eslint-disable-next-line no-unused-expressions
      before;
    }

    // =====================================================================
    // LAGU KARUT PILIHAN JURI — grouped by "Yengki" / "Matang" sections.
    // Each row is the "Kesesuaian...irama dengan <tail>" line whose tail is
    // relabeled per judge (default keeps the seeded "seni kata").
    // =====================================================================
    if (category.name === 'Lagu Karut Pilihan Juri') {
      const tail = (IRAMA_SUFFIX_BY_CAT_AND_JUDGE[category.name] || {})[userName];
      const label = (name) => {
        const base = name.replace(/^(Yengki|Matang) - /, '');
        if (base.startsWith('Keaslian dan kreativiti')) {
          return userName === 'Elfee' ? 'Keaslian dan kreativiti irama lagu' : base;
        }
        return tail ? base.replace(/dengan .*/, `dengan ${tail}`) : base;
      };
      const yengki = subcategories.filter(
        (s) => s.name.startsWith('Yengki - ') && isVisibleSubcat(s),
      );
      const matang = subcategories.filter(
        (s) => s.name.startsWith('Matang - ') && isVisibleSubcat(s),
      );
      return (
        <>
          {renderGroup('Yengki', yengki, label)}
          {renderGroup('Matang', matang, label)}
        </>
      );
    }

    // Default: flat list (with per-judge name override for the irama subcat
    // in "Lagu Juara / Lagu Karut Pilihan Juri").
    const overrideName = (subcat) => {
      if (!SIMPLE_SUM_CATS.has(category.name)) return subcat;
      if (subcat.name.startsWith('Kesesuaian dan keberkesanan irama dengan')) {
        const tail = (IRAMA_SUFFIX_BY_CAT_AND_JUDGE[category.name] || {})[userName];
        if (tail) {
          return { ...subcat, name: `Kesesuaian dan keberkesanan irama dengan ${tail}` };
        }
      }
      if (subcat.name === 'Keaslian dan kreativiti') {
        return { ...subcat, name: 'Keaslian dan kreativiti irama lagu/muzik' };
      }
      return subcat;
    };
    return subcategories.map((subcat) => {
      if (!isVisibleSubcat(subcat)) return null;
      const display = overrideName(subcat);
      return (
        <SubcategoryInput
          key={subcat.id}
          subcategory={display}
          value={scores[subcat.id] ?? null}
          onChange={(val) => setScores({ ...scores, [subcat.id]: val })}
        />
      );
    });
  };

  return (
    <section
      className="space-y-4"
      data-testid={`category-form-${category.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest">
            Kategori {index + 1} daripada {total}
          </div>
          <div className="text-xl text-[#DFFF00] font-heading">{category.name}</div>
        </div>
        {hasScored && (
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm">
            <Check className="h-4 w-4" />
            Sudah Dimarkahkan
          </div>
        )}
      </div>

      <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm space-y-4">
        <div className="flex justify-between items-center pb-3 border-b border-zinc-800">
          <div className="text-sm text-zinc-400 uppercase tracking-wider">Subkategori</div>
          <div className="font-mono text-lg text-[#DFFF00]">
            {subcategoryTotal.toFixed(2)} / {visibleMaxScore}
          </div>
        </div>

        <div className="space-y-1">{renderSubcategories()}</div>

        {/* Bengkel Participation — Seni Kata only, read-only for judge */}
        {category.name === 'Seni Kata' && (
          <div className="pt-4 mt-2 border-t border-zinc-800 space-y-3">
            <div className="bg-zinc-900/70 border border-amber-500/30 rounded-sm px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-amber-300">
                    Markah Penyertaan Bengkel
                  </div>
                  <div className="text-xs text-zinc-500">Ditetapkan oleh admin — hanya untuk paparan</div>
                </div>
                <div className="font-mono text-lg text-amber-200 tabular-nums">
                  {bengkelMarks || 0} / 10
                </div>
              </div>
            </div>
            <div className="bg-[#DFFF00]/5 border border-[#DFFF00]/30 rounded-sm px-4 py-3">
              <div className="flex items-center justify-between gap-3 text-xs text-zinc-400">
                <div>Pengiraan akhir Seni Kata</div>
                <div className="font-mono text-zinc-300">
                  ({subcategoryTotal.toFixed(2)} × 0.9) + {bengkelMarks || 0}
                </div>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <div className="text-sm font-semibold text-white">Jumlah Kategori (dengan Bengkel)</div>
                <div className="font-mono text-2xl font-bold text-[#DFFF00] tabular-nums">
                  {(subcategoryTotal * 0.9 + (bengkelMarks || 0)).toFixed(2)} / 100
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Deduction */}
        <div className="pt-4 border-t border-zinc-800">
          <div className="flex items-center justify-between py-2">
            <div className="flex-1">
              <label className="text-sm text-zinc-300 font-semibold">Potongan</label>
              <div className="text-xs text-zinc-500">Markah untuk ditolak daripada jumlah</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={() => setDeduction(Math.max(0, deduction - 0.5))}
                className="h-8 w-8 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm p-0"
              >
                -
              </Button>
              <NumericInput
                value={deduction}
                onChange={setDeduction}
                ariaLabel="Potongan"
                className="w-16 bg-zinc-900 border border-zinc-700 focus:border-[#DFFF00] focus:outline-none text-center text-white font-mono rounded-sm py-1"
              />
              <Button
                type="button"
                onClick={() => setDeduction(deduction + 0.5)}
                className="h-8 w-8 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm p-0"
              >
                +
              </Button>
            </div>
          </div>
          {deduction > 0 && (
            <div className="mt-2 flex justify-between items-center text-sm">
              <span className="text-zinc-400">Selepas potongan:</span>
              <span className="font-mono text-lg text-[#DFFF00]">
                {currentTotal.toFixed(2)} / {visibleMaxScore}
              </span>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="pt-4 border-t border-zinc-800">
          <label className="text-sm text-zinc-300 font-semibold block mb-2">Catatan (Pilihan)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Tambah komen atau catatan di sini..."
            className="w-full bg-zinc-900 border border-zinc-700 focus:border-[#DFFF00] focus:outline-none text-white rounded-sm p-3 min-h-[80px] resize-none"
            data-testid={`notes-field-${category.id}`}
          />
        </div>
      </div>

      {validationError && (
        <div
          className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-red-400 text-sm"
          data-testid={`validation-error-${category.id}`}
        >
          {validationError}
        </div>
      )}

      <div className="space-y-2">
        <Button
          onClick={handleSubmit}
          data-testid={`submit-score-button-${category.id}`}
          disabled={loading || locked}
          className={`w-full font-semibold uppercase tracking-widest py-6 rounded-sm transition-transform hover:-translate-y-0.5 ${
            hasScored
              ? 'bg-green-500 hover:bg-green-500/90 text-black'
              : 'bg-[#DFFF00] hover:bg-[#DFFF00]/90 text-black'
          }`}
        >
          {loading
            ? 'Menghantar...'
            : locked
            ? 'Pemarkahan Dikunci'
            : hasScored
            ? `Kemaskini Markah ${category.name}`
            : `Hantar Markah ${category.name}`}
        </Button>
        <p className="text-xs text-zinc-500 text-center">
          Pastikan anda menekan{' '}
          <span className="text-[#DFFF00] font-semibold">
            {hasScored ? 'Kemaskini' : 'Hantar'} Markah
          </span>{' '}
          sebelum beralih ke kategori seterusnya supaya markah anda disimpan.
        </p>
      </div>
    </section>
  );
}

export default function JudgeDashboard({ user, onLogout }) {
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});
  const [myScores, setMyScores] = useState([]);
  const [bengkelByTeam, setBengkelByTeam] = useState({});
  const [pendingTeamId, setPendingTeamId] = useState(null);
  const [scoringLocked, setScoringLocked] = useState(false);
  const [showMyRankings, setShowMyRankings] = useState(false);
  const [myRankings, setMyRankings] = useState([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);

  // Redirect admin to admin dashboard if they land here by mistake
  useEffect(() => {
    if (user.role === 'admin') {
      window.location.href = '/admin';
    }
  }, [user]);

  // Read URL query params to auto-open a team (enables multi-tab judging)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const genderParam = params.get('gender');
    const teamIdParam = params.get('team');
    const viewParam = params.get('view');
    if (viewParam === 'rankings') {
      setShowMyRankings(true);
    }
    if (genderParam === 'Boys' || genderParam === 'Girls') {
      setSelectedGender(genderParam);
    }
    if (teamIdParam) {
      setPendingTeamId(teamIdParam);
    }
  }, []);

  useEffect(() => {
    fetchAssignedCategories();
  }, []);

  useEffect(() => {
    if (selectedGender) {
      fetchTeams(selectedGender);
    }
  }, [selectedGender]);

  // Once teams are loaded and there's a pending team ID from URL, select it
  useEffect(() => {
    if (pendingTeamId && teams.length > 0) {
      const match = teams.find((t) => t.id === pendingTeamId);
      if (match) {
        setSelectedTeam(match);
      }
      setPendingTeamId(null);
    }
  }, [teams, pendingTeamId]);

  // Load subcategories for every assigned category once categories are known
  useEffect(() => {
    if (categories.length > 0) {
      fetchAllSubcategories();
    }
  }, [categories]);

  useEffect(() => {
    if (categories.length > 0) {
      fetchMyScores();
    }
  }, [categories]);

  // Re-fetch scores when the window regains focus — needed when a judge scores
  // a team in another tab and returns to this team-selection tab.
  useEffect(() => {
    const handleFocus = () => {
      if (categories.length > 0) {
        fetchMyScores();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [categories]);

  // Update browser tab title to team name so judges can identify multiple tabs
  useEffect(() => {
    if (selectedTeam) {
      document.title = `${selectedTeam.name} - Piala Suara Serumpun 2026`;
    } else {
      document.title = 'Piala Suara Serumpun 2026';
    }
    return () => {
      document.title = 'Piala Suara Serumpun 2026';
    };
  }, [selectedTeam]);

  const fetchAssignedCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/categories/assigned`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data);
    } catch (error) {
      toast.error('Gagal memuatkan kategori');
    }
  };

  const fetchTeams = async (gender) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teams?gender=${gender}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeams(response.data);
    } catch (error) {
      toast.error('Gagal memuatkan pasukan');
    }
  };

  const fetchAllSubcategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const results = await Promise.all(
        categories.map((cat) =>
          axios.get(`${API}/subcategories/${cat.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      const map = {};
      categories.forEach((cat, i) => {
        map[cat.id] = results[i].data;
      });
      setSubcategoriesByCategory(map);
    } catch (error) {
      toast.error('Gagal memuatkan subkategori');
    }
  };

  const fetchMyScores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/scores/my-scores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyScores(response.data);
    } catch (error) {
      toast.error('Gagal memuatkan markah');
    }
    try {
      const token = localStorage.getItem('token');
      const b = await axios.get(`${API}/bengkel-participation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const map = {};
      (b.data || []).forEach((r) => { map[r.team_id] = Number(r.marks) || 0; });
      setBengkelByTeam(map);
    } catch (error) {
      // silent — bengkel row is optional
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/public`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScoringLocked(Boolean(response.data.scoring_locked));
    } catch (error) {
      // silent — non-critical
    }
  };

  useEffect(() => {
    fetchSettings();
    const iv = setInterval(fetchSettings, 8000);
    return () => clearInterval(iv);
  }, []);

  const handleBackToTeamSelection = () => {
    setSelectedTeam(null);
    // Clean URL params so refresh/back doesn't re-open the same team
    if (window.location.search) {
      window.history.replaceState({}, '', '/judge');
    }
  };

  const fetchMyRankings = async () => {
    setRankingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/scores/my-rankings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyRankings(res.data || []);
    } catch (e) {
      toast.error('Gagal memuatkan kedudukan');
    } finally {
      setRankingsLoading(false);
    }
  };

  useEffect(() => {
    if (showMyRankings) fetchMyRankings();
  }, [showMyRankings]);

  // Shared handlers so every screen can reach them.
  const handleChangeGender = () => {
    setSelectedGender(null);
    setSelectedTeam(null);
    setShowMyRankings(false);
    if (window.location.search) {
      window.history.replaceState({}, '', '/judge');
    }
  };

  if (showMyRankings) {
    return (
      <div className="min-h-screen bg-[#09090b] flex flex-col">
        <JudgeNav
          user={user}
          onLogout={onLogout}
          onChangeTeam={handleBackToTeamSelection}
          onChangeGender={handleChangeGender}
          onBack={() => setShowMyRankings(false)}
          backLabel="Kembali"
          sticky
        />
        <div className="flex-1 max-w-4xl w-full mx-auto p-4 py-10 space-y-8">
          <div className="text-center space-y-2">
            <div className="text-sm text-zinc-500 uppercase tracking-widest">Pemarkahan Peribadi</div>
            <h2 className="font-heading text-3xl font-bold text-white">Kedudukan Anda</h2>
            <p className="text-zinc-400 text-sm">
              Kedudukan pasukan dalam kategori yang anda markahkan, berdasarkan markah anda sahaja (tanpa pemberat dan tanpa gabungan dengan pengadil lain).
            </p>
          </div>

          {rankingsLoading && (
            <div className="text-center text-zinc-400">Memuatkan...</div>
          )}

          {!rankingsLoading && myRankings.length === 0 && (
            <div className="text-center text-zinc-500 italic">Tiada kategori ditugaskan kepada anda.</div>
          )}

          {myRankings.map((cat) => (
            <div
              key={cat.category_id}
              className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm"
              data-testid={`my-ranking-${cat.category_name}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-2xl font-bold text-white">
                  {cat.category_name}
                </h3>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">
                  Markah Mentah Anda
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[
                  { key: 'boys', label: 'Lelaki', icon: '♂', list: cat.boys },
                  { key: 'girls', label: 'Perempuan', icon: '♀', list: cat.girls },
                ].map((g) => (
                  <div key={g.key}>
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="text-2xl">{g.icon}</span> {g.label}
                    </h4>
                    <div className="space-y-2">
                      {g.list.length === 0 && (
                        <div className="text-sm text-zinc-500 italic">Tiada pasukan.</div>
                      )}
                      {g.list.map((t) => (
                        <div
                          key={t.team_id}
                          className={`bg-zinc-900 border ${
                            t.scored ? 'border-zinc-800' : 'border-zinc-800/50 opacity-60'
                          } p-3 rounded-sm flex items-center gap-3`}
                        >
                          <div
                            className={`h-10 w-10 flex items-center justify-center rounded-full font-bold ${
                              t.rank === 1
                                ? 'bg-[#DFFF00] text-black'
                                : t.rank === 2
                                ? 'bg-zinc-700 text-white'
                                : t.rank === 3
                                ? 'bg-zinc-800 text-white'
                                : 'bg-zinc-900 text-zinc-400 border border-zinc-700'
                            }`}
                          >
                            {t.rank ?? '–'}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-white">{t.team_name}</div>
                            <div className="text-xs text-zinc-500">
                              {t.scored ? '' : 'Belum dimarkahkan'}
                            </div>
                          </div>
                          <div className="font-mono text-[#DFFF00]">
                            {t.scored ? `${t.score.toFixed(2)} / ${cat.max_score}` : '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!selectedGender) {
    return (
      <div className="min-h-screen bg-[#09090b] flex flex-col">
        <JudgeNav
          user={user}
          onLogout={onLogout}
          onChangeTeam={handleBackToTeamSelection}
          onChangeGender={handleChangeGender}
        />

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center space-y-2">
              <h2 className="font-heading text-3xl font-bold text-white">Pilih Jantina Pasukan</h2>
              <p className="text-zinc-400">Pilih pasukan yang ingin anda markahkan</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => setSelectedGender('Boys')}
                data-testid="select-boys-button"
                className="h-32 bg-[#18181b] hover:bg-zinc-800 border border-zinc-700 flex flex-col gap-2 text-white"
              >
                <div className="text-4xl">♂</div>
                <div className="font-heading text-xl">Lelaki</div>
              </Button>
              <Button
                onClick={() => setSelectedGender('Girls')}
                data-testid="select-girls-button"
                className="h-32 bg-[#18181b] hover:bg-zinc-800 border border-zinc-700 flex flex-col gap-2 text-white"
              >
                <div className="text-4xl">♀</div>
                <div className="font-heading text-xl">Perempuan</div>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Team selection screen
  if (!selectedTeam) {
    return (
      <div className="min-h-screen bg-[#09090b] flex flex-col">
        <JudgeNav
          user={user}
          subtitle={`${user.name} - ${selectedGender === 'Boys' ? 'Lelaki' : 'Perempuan'}`}
          onLogout={onLogout}
          onChangeTeam={handleBackToTeamSelection}
          onChangeGender={handleChangeGender}
        />

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl space-y-6">
            <div className="text-center space-y-2">
              <h2 className="font-heading text-3xl font-bold text-white">Pilih Pasukan untuk Dimarkahkan</h2>
              <p className="text-zinc-400">Setiap pasukan akan dibuka dalam tab baharu supaya anda boleh memarkah beberapa pasukan secara serentak</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teams.map((team) => {
                const teamScores = myScores.filter(s => s.team_id === team.id);
                const totalCategoriesForTeam = categories.length;
                const scoredCategories = teamScores.length;
                const isComplete = scoredCategories === totalCategoriesForTeam;
                
                return (
                  <Button
                    key={team.id}
                    onClick={() => {
                      const url = `${window.location.origin}/judge?gender=${encodeURIComponent(selectedGender)}&team=${encodeURIComponent(team.id)}`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    data-testid={`select-team-${team.name.toLowerCase().replace(' ', '-')}`}
                    className="min-h-40 h-auto py-5 px-4 bg-[#18181b] hover:bg-zinc-800 border border-zinc-700 flex flex-col items-center justify-center gap-3 text-white relative overflow-hidden"
                  >
                    {isComplete && (
                      <div className="absolute top-2 right-2">
                        <Check className="h-5 w-5 text-green-400" />
                      </div>
                    )}
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-[#DFFF00] text-black font-heading font-black text-xl shadow-md shrink-0 tabular-nums">
                      {team.display_order ?? '•'}
                    </div>
                    <div className="font-heading text-xl md:text-2xl font-bold text-center leading-tight px-2 w-full break-words whitespace-normal">
                      {team.name}
                    </div>
                    <div className="text-xs text-zinc-400">
                      {scoredCategories}/{totalCategoriesForTeam} kategori dimarkahkan
                    </div>
                    <div className="w-3/4 bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#DFFF00] transition-all"
                        style={{ width: `${(scoredCategories / totalCategoriesForTeam) * 100}%` }}
                      />
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const subcategoriesReady =
    categories.length > 0 &&
    categories.every((c) => (subcategoriesByCategory[c.id] || []).length > 0);

  if (teams.length === 0 || !subcategoriesReady) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-zinc-400">Memuatkan...</div>
      </div>
    );
  }

  const totalScored = myScores.filter(
    (s) => s.team_id === selectedTeam.id && categories.some((c) => c.id === s.category_id)
  ).length;
  const totalRequired = categories.length;

  return (
    <div className="min-h-screen bg-[#09090b] flex flex-col">
      <JudgeNav
        user={user}
        subtitle={`${user.name} - ${selectedGender === 'Boys' ? 'Lelaki' : 'Perempuan'}`}
        onLogout={onLogout}
        onChangeTeam={handleBackToTeamSelection}
        onChangeGender={handleChangeGender}
        sticky
      />

      <div className="flex-1 flex flex-col items-center p-4 py-10">
        <div className="w-full max-w-2xl space-y-8">
          <div className="text-center space-y-2">
            <div className="text-sm text-zinc-500 uppercase tracking-widest">
              Memarkah {totalRequired} {totalRequired === 1 ? 'Kategori' : 'Kategori'}
            </div>
            <h2 className="font-heading text-4xl font-black text-white" data-testid="current-team-name">
              {selectedTeam.name}
            </h2>
            <div className="text-sm text-zinc-400">
              {totalScored} daripada {totalRequired} kategori telah dimarkahkan
            </div>
            <div className="mt-2 w-full bg-zinc-800 h-1 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#DFFF00] transition-all duration-300"
                style={{
                  width: `${(totalScored / totalRequired) * 100}%`,
                }}
              />
            </div>
          </div>

          {scoringLocked && (
            <div
              className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-sm px-4 py-3 text-center"
              data-testid="scoring-locked-banner"
            >
              Pemarkahan sedang dikunci oleh admin. Anda tidak boleh menghantar atau mengemaskini markah pada masa ini.
            </div>
          )}

          {categories.map((cat, idx) => {
            const existingScore = myScores.find(
              (s) => s.team_id === selectedTeam.id && s.category_id === cat.id
            );
            return (
              <CategoryScoringForm
                key={`${selectedTeam.id}-${cat.id}`}
                category={cat}
                subcategories={subcategoriesByCategory[cat.id] || []}
                existingScore={existingScore}
                userName={user.name}
                teamId={selectedTeam.id}
                onSaved={fetchMyScores}
                index={idx}
                total={categories.length}
                locked={scoringLocked}
                bengkelMarks={cat.name === 'Seni Kata' ? (bengkelByTeam[selectedTeam.id] || 0) : null}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
